<ion-header class="modal-header">
  <div class="modal-title">
    <h3>{{ id ? "Edit Custom Field" : "Create a Custom Field" }}</h3>
  </div>
  <ion-toolbar>
    <ion-buttons slot="end">
      <ion-button
        fill="clear"
        shape="round"
        class="close-button"
        (click)="dismiss()"
      >
        <ion-icon name="close"></ion-icon>
      </ion-button>
    </ion-buttons>
  </ion-toolbar>
</ion-header>

<ion-content>
  <div class="custom-field-container">
    <div class="custom-field-section">
      <h2 class="input-section-header">Configure</h2>
      <div class="input-section-divider"></div>

      <app-custom-fields
        *ngIf="field"
        [customFields]="[field]"
        [placeholder]="'Ex: Favorite color'"
        [addCustomFieldButtonLabel]="'Add a custom ticket field'"
        [showDescription]="true"
        [hideAddAndDeleteButtons]="true"
        [showRequiredOption]="false"
        [editMode]="!!id"
        [readOnly]="false"
        [projectId]="projectId"
      >
      </app-custom-fields>
    </div>

    <div class="custom-field-section-extended" *ngIf="field?.label">
      <h2 class="input-section-header">Preview</h2>
      <div class="input-section-divider"></div>

      <div class="preview-container preview-section">
        <ion-card class="outline-card">
          <app-response-input
            [responseInputFields]="[field]"
            [showPublicOption]="false"
            layout="list"
          >
          </app-response-input>
        </ion-card>
      </div>
    </div>

    <div
      class="custom-field-section-extended"
      *ngIf="
        (field?.type === CUSTOM_FIELD_TYPE_SINGLE_SELECT ||
          field?.type === CUSTOM_FIELD_TYPE_MULTI_SELECT) &&
        !field?.externalSelect
      "
    >
      <h2 class="input-section-header">Conditional Options</h2>
      <div class="input-section-divider"></div>
      <ion-label class="input-label">
        Show different options based on conversation context
        <ion-icon
          name="information-circle"
          class="icon-padded-left"
          ngxTippy
          data-tippy-content="When enabled, different options will be shown based on conversation context (e.g., ticket type). Configure conditions to determine which options appear in different scenarios."
        ></ion-icon>
      </ion-label>
      <ion-select
        class="select-outline input-field"
        [(ngModel)]="conditionalOptionsMode"
        (ionChange)="onConditionalOptionsModeChange()"
        interface="popover"
        placeholder="Select mode"
      >
        <ion-select-option [value]="false">Disabled</ion-select-option>
        <ion-select-option [value]="true">Enabled</ion-select-option>
      </ion-select>

      <div *ngIf="conditionalOptionsMode === true">
        <app-branching-editor
          [config]="branchingConfig"
          [filterOptions]="filterOptions"
          [projectId]="projectId"
          (configChange)="onBranchingConfigChange($event)"
        >
          <ng-template #stepTemplate let-branch="branch">
            <div class="conditional-options-wrapper">
              <ion-list class="no-padding" style="background: none">
                <ion-reorder-group
                  (ionItemReorder)="onConditionalOptionsReorder($event, branch)"
                  [disabled]="false"
                  class="conditional-options-reorder-group"
                >
                  <div
                    *ngFor="let option of branch.steps; let i = index"
                    class="dropdown-option-item flex-space-between conditional-option-item-layout"
                  >
                    <ion-label class="conditional-option-label">{{
                      option
                    }}</ion-label>
                    <div slot="end" class="flexed">
                      <ion-buttons>
                        <ion-button
                          (click)="removeOption(branch, i)"
                          fill="clear"
                          color="dark"
                          class="item-accessory-button"
                          ngxTippy
                          data-tippy-content="Remove option"
                        >
                          <ion-icon name="close-circle"></ion-icon>
                        </ion-button>
                      </ion-buttons>
                      <ion-reorder></ion-reorder>
                    </div>
                  </div>
                </ion-reorder-group>
              </ion-list>
              <ion-select
                #optionSelect
                class="hidden-select"
                [interfaceOptions]="{ header: 'Select Options' }"
                interface="popover"
                [multiple]="true"
                [(ngModel)]="branch.steps"
                (ionChange)="onConditionalOptionsChange()"
                okText="Okay"
                cancelText="Dismiss"
              >
                <ion-select-option
                  *ngFor="let option of field.options"
                  [value]="option"
                >
                  {{ option }}
                </ion-select-option>
              </ion-select>
              <ion-button
                fill="clear"
                (click)="optionSelect.open($event)"
                class="add-option-button"
              >
                <ion-icon name="add-circle-outline" slot="start"></ion-icon>
                Add/Edit Options
              </ion-button>
            </div>
          </ng-template>
        </app-branching-editor>
      </div>
    </div>

    <div class="custom-field-section-extended">
      <h2 class="input-section-header">Slack Post Options</h2>
      <div class="input-section-divider"></div>

      <ion-label class="input-label">
        Show in Initial Slack Message
        <ion-icon
          name="information-circle"
          class="icon-padded-left"
          ngxTippy
          data-tippy-content="Choose where this field will be displayed to users after they submit a ticket. Options are the initial message that is posted or the confirmation message that is posted into the thread."
        ></ion-icon>
      </ion-label>
      <ion-select
        class="select-outline input-field"
        [(ngModel)]="field.showInPostTicketMessage"
        interface="popover"
        placeholder="Select"
        [compareWith]="showInPostTicketMessageCompareFn"
      >
        <ion-select-option [value]="null">None</ion-select-option>
        <ion-select-option
          [value]="SHOW_IN_POST_TICKET_MESSAGE_VALUES.INITIAL_MESSAGE"
          >Initial Message</ion-select-option
        >
        <ion-select-option
          [value]="SHOW_IN_POST_TICKET_MESSAGE_VALUES.CONFIRMATION_MESSAGE"
          >Confirmation Message</ion-select-option
        >
      </ion-select>

      <ion-label class="input-label">
        Show in Triage Message
        <ion-icon
          name="information-circle"
          class="icon-padded-left"
          ngxTippy
          data-tippy-content="When enabled, this field will be displayed in triage messages for better context."
        ></ion-icon>
      </ion-label>
      <ion-select
        class="select-outline input-field"
        [(ngModel)]="field.showInTriageMessages"
        interface="popover"
        placeholder="Select"
        [disabled]="isLoading"
      >
        <ion-select-option [value]="false">No</ion-select-option>
        <ion-select-option [value]="true">Yes</ion-select-option>
      </ion-select>
    </div>

    <div class="custom-field-section-extended">
      <h2 class="input-section-header">AI Options</h2>
      <div class="input-section-divider"></div>
      <ion-label class="input-label">
        Prefill Value on Status Change
        <ion-icon
          name="information-circle"
          class="icon-padded-left"
          ngxTippy
          data-tippy-content="When a ticket is updated to this status, or a downstream status, the AI will attempt to prefill this value."
        ></ion-icon>
      </ion-label>
      <button
        class="select-outline input-field fake-form-select"
        (click)="showSubStatusPopover($event)"
        [disabled]="isLoading"
        expand="block"
        fill="outline"
      >
        <span>
          <ion-icon
            *ngIf="selectedSubStatusDisplay?.icon"
            [name]="selectedSubStatusDisplay?.icon"
            [class]="selectedSubStatusDisplay?.iconClass"
            slot="start"
            class="status-icon"
          ></ion-icon>
          <ion-label *ngIf="selectedSubStatusDisplay">{{
            selectedSubStatusDisplay.name
          }}</ion-label>
          <ion-label *ngIf="!selectedSubStatusDisplay">None</ion-label>
        </span>
        <ion-icon name="caret-down-outline" slot="end"></ion-icon>
      </button>
    </div>

    <div class="custom-field-section-extended">
      <ion-label class="input-label">
        Prefill Prompt
        <ion-icon
          name="information-circle"
          class="icon-padded-left"
          ngxTippy
          data-tippy-content="Enter a prompt for the AI to use when prefilling this field. This will guide the AI in generating a relevant value."
        ></ion-icon>
      </ion-label>
      <ion-textarea
        class="input-field"
        [(ngModel)]="field.prefillPrompt"
        placeholder="e.g., Summarize the issue based on the initial customer message."
        [autoGrow]="true"
        rows="3"
      ></ion-textarea>
    </div>

    <div class="custom-field-section-extended">
      <h2 class="input-section-header">Status Requirements</h2>
      <div class="input-section-divider"></div>
      <ion-label class="input-label">
        Make this field required when a ticket transitions to this status or any
        downstream status.
        <ion-icon
          name="information-circle"
          class="icon-padded-left"
          ngxTippy
          data-tippy-content="Users won't be able to change status until this field is filled. Does not apply to service accounts or automations, and users can still empty the field after the status is changed."
        ></ion-icon>
      </ion-label>
      <button
        class="select-outline input-field fake-form-select"
        (click)="showRequiredSubStatusPopover($event)"
        [disabled]="isLoading"
        expand="block"
        fill="outline"
      >
        <span>
          <ion-icon
            *ngIf="selectedRequiredSubStatusDisplay?.icon"
            [name]="selectedRequiredSubStatusDisplay?.icon"
            [class]="selectedRequiredSubStatusDisplay?.iconClass"
            slot="start"
            class="status-icon"
          ></ion-icon>
          <ion-label *ngIf="selectedRequiredSubStatusDisplay">{{
            selectedRequiredSubStatusDisplay.name
          }}</ion-label>
          <ion-label *ngIf="!selectedRequiredSubStatusDisplay">None</ion-label>
        </span>
        <ion-icon name="caret-down-outline" slot="end"></ion-icon>
      </button>
    </div>
  </div>
  <div style="padding: 0 16px 16px 16px; text-align: center" *ngIf="id">
    <ion-button fill="clear" color="dark" (click)="toggleConversations()">
      <ion-label
        >{{ isShowingConversations ? "Hide" : "Show" }} conversations using this
        field</ion-label
      >
      <ion-icon
        name="chevron-down-outline"
        slot="end"
        *ngIf="!isShowingConversations"
      ></ion-icon>
      <ion-icon
        name="chevron-up-outline"
        slot="end"
        *ngIf="isShowingConversations"
      ></ion-icon>
    </ion-button>
  </div>
  <div
    class="custom-field-section-extended"
    *ngIf="id && conversationsDataSource && isShowingConversations"
  >
    <h2 class="input-section-header">Conversations Using This Field</h2>
    <div class="input-section-divider"></div>
    <ngx-datatable
      class="material datatable-row-borders datatable-row-top-aligned"
      [loadingIndicator]="isConversationsLoading"
      columnMode="force"
      paginated
      [dataSource]="conversationsDataSource"
      [limit]="10"
      [headerHeight]="50"
      [footerHeight]="50"
      rowHeight="auto"
      [scrollbarH]="true"
      [rows]="conversationsWithField"
      [reorderable]="false"
    >
      <ngx-datatable-column
        name="Conversation ID"
        prop="friendlyId"
        [width]="100"
      >
        <ng-template let-row="row" ngx-datatable-cell-template>
          <a
            [routerLink]="['/dashboard/inbox/all/conversations', row.id]"
            target="_blank"
            >#{{ row.friendlyId }} {{ row.title }}</a
          >
        </ng-template>
      </ngx-datatable-column>
      <ngx-datatable-column
        name="Field Value"
        prop="ticketFieldValue"
        [minWidth]="200"
      >
        <ng-template let-row="row" ngx-datatable-cell-template>
          <div class="suggested-field-value">
            {{ row.ticketFieldValue }}
          </div>
        </ng-template>
      </ngx-datatable-column>

      <ngx-datatable-column
        name="Feedback"
        prop="ticketFieldFeedback"
        [width]="180"
      >
        <ng-template let-row="row" ngx-datatable-cell-template>
          <div
            *ngIf="
              row.ticketFieldFeedback !== null &&
              row.ticketFieldFeedback !== undefined
            "
          >
            {{ row.ticketFieldFeedback ? "Approved" : "Rejected" }}
          </div>
          <div
            *ngIf="
              row.ticketFieldFeedback === null ||
              row.ticketFieldFeedback === undefined
            "
          >
            <a
              (click)="showPrefillOption(row)"
              class="prefill-button"
              ngxTippy
              data-tippy-content="click to apply or reject prefill"
              ><ion-icon
                name="add-outline"
                class="padded-icon checkbox-icon"
              ></ion-icon
              >add feedback</a
            >
          </div>
        </ng-template>
      </ngx-datatable-column>

      <ngx-datatable-column
        name="AI Analysis"
        prop="prefillAnalysis"
        [minWidth]="250"
      >
        <ng-template let-row="row" ngx-datatable-cell-template>
          <!-- View mode -->
          <div
            *ngIf="row.prefillAnalysis && row.id !== editingRowId"
            class="prefill-analysis"
          >
            <div class="analysis-actions">
              <a (click)="startEditingAnalysis(row)" class="edit-link">
                <ion-icon name="create-outline" class="padded-icon"></ion-icon
                >Edit
              </a>
              <span
                *ngIf="row.isSupportTeamMemberAnalysis"
                class="support-edited-badge"
              >
                <ion-icon name="person-outline" class="padded-icon"></ion-icon
                >Edited by Support
              </span>
            </div>
            <div
              [innerHTML]="renderMarkdown(row.prefillAnalysis) | safeHtml"
            ></div>
          </div>

          <!-- Edit mode -->
          <div *ngIf="row.id === editingRowId" class="edit-analysis-container">
            <app-tiptap-editor
              [(ngModel)]="editingAnalysis"
              placeholder="Enter analysis..."
              [enableLinkEditing]="true"
            >
            </app-tiptap-editor>

            <div class="edit-actions">
              <ion-button
                size="small"
                fill="clear"
                (click)="cancelEditingAnalysis()"
                [disabled]="isSavingAnalysis"
              >
                Cancel
              </ion-button>
              <ion-button
                size="small"
                (click)="saveAnalysis(row)"
                [disabled]="isSavingAnalysis || !editingAnalysis"
              >
                Save
                <ion-spinner
                  *ngIf="isSavingAnalysis"
                  name="crescent"
                  class="spinner-spacing"
                ></ion-spinner>
              </ion-button>
            </div>
          </div>

          <!-- States for when no analysis exists -->
          <div
            *ngIf="
              !row.prefillAnalysis &&
              (row.ticketFieldFeedback === true ||
                row.ticketFieldFeedback === false)
            "
            class="pending-analysis"
          >
            <ion-icon name="time-outline" class="padded-icon"></ion-icon>
            Analysis pending...
          </div>
          <div
            *ngIf="
              !row.prefillAnalysis &&
              (row.ticketFieldFeedback === null ||
                row.ticketFieldFeedback === undefined)
            "
            class="no-feedback"
          >
            <span>Add feedback for AI analysis</span>
          </div>
        </ng-template>
      </ngx-datatable-column>
    </ngx-datatable>
  </div>
</ion-content>

<ion-footer class="footer flex-space-between">
  <ion-button (click)="submit()" [disabled]="isLoading">
    Save
    <ion-spinner
      name="crescent"
      [hidden]="!isLoading"
      class="spinner-spacing"
    ></ion-spinner>
  </ion-button>
  <div>
    <ion-button
      fill="clear"
      color="dark"
      (click)="duplicate()"
      [disabled]="isLoading"
      *ngIf="id"
      ngxTippy
      data-tippy-content="Duplicate"
      class="footer-button-spacing"
    >
      <ion-icon name="duplicate-outline"></ion-icon>
    </ion-button>
    <ion-button
      fill="clear"
      color="dark"
      (click)="delete()"
      [disabled]="isLoading"
      *ngIf="id"
      ngxTippy
      data-tippy-content="Delete"
    >
      <ion-icon name="trash-outline"></ion-icon>
    </ion-button>
  </div>
</ion-footer>
